import { CommonModule } from "@angular/common"
import { Component, Inject, OnInit, inject, signal } from "@angular/core"
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms"
import { MatAutocompleteModule } from "@angular/material/autocomplete"
import { MatButtonModule } from "@angular/material/button"
import { MatNativeDateModule } from "@angular/material/core"
import { MatDatepickerModule } from "@angular/material/datepicker"
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatInputModule } from "@angular/material/input"
import { MatSelectModule } from "@angular/material/select"
import { MatSlideToggleModule } from "@angular/material/slide-toggle"
import { MatSnackBar } from "@angular/material/snack-bar"
import { MatTabsModule } from "@angular/material/tabs"

import { debounceTime, startWith, switchMap } from "rxjs"
import { formatDateForAPI } from "../../core/adapters/local-date-adapter"

import { DepreciationMethod, FixedAsset } from "../../core/models/asset.model"
import { Entity } from "../../core/models/entity.model"
import { LedgerAccount, LedgerAccountType } from "../../core/models/ledger.model"
import { AssetService } from "../../core/services/asset.service"
import { EntityService } from "../../core/services/entity.service"
import { LedgerService } from "../../core/services/ledger.service"

@Component({
  selector: "app-asset-detail",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    MatTabsModule,
  ],
  templateUrl: "./asset-detail.component.html",
  styleUrls: ["./asset-detail.component.css"],
})
export class AssetDetailComponent implements OnInit {
  private fb = inject(FormBuilder)
  private assetService = inject(AssetService)
  private ledgerService = inject(LedgerService)
  private entityService = inject(EntityService)
  private snackBar = inject(MatSnackBar)

  form: FormGroup
  isEditMode = false
  depreciationMethods = Object.values(DepreciationMethod)

  entities = signal<Entity[]>([])
  filteredAssetAccounts = signal<LedgerAccount[]>([])
  filteredDepreciationAccounts = signal<LedgerAccount[]>([])

  constructor(
    public dialogRef: MatDialogRef<AssetDetailComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { asset?: FixedAsset; transaction?: any },
  ) {
    this.isEditMode = !!data.asset

    // Pre-fill from transaction if provided
    let initialName = data.asset?.name || ""
    let initialPrice = data.asset?.purchasePrice || ""
    let initialDate = data.asset?.purchaseDate ? new Date(data.asset.purchaseDate) : new Date()
    let initialEntityId = data.asset?.entityId || ""

    if (data.transaction) {
      // Pre-fill from transaction
      initialName = data.transaction.remoteName || data.transaction.purposeText || ""
      initialPrice = Math.abs(data.transaction.amount) || ""
      initialDate = new Date(data.transaction.bookingDate)
      initialEntityId = data.transaction.entityId || ""
    }

    this.form = this.fb.group({
      name: [initialName, [Validators.required]],
      assetNumber: [data.asset?.assetNumber || ""],
      description: [data.asset?.description || ""],
      entityId: [initialEntityId, [Validators.required]],

      purchaseDate: [initialDate, [Validators.required]],
      purchasePrice: [initialPrice, [Validators.required, Validators.min(0)]],
      salvageValue: [data.asset?.salvageValue || 0, [Validators.min(0)]],
      usefulLifeMonths: [
        data.asset?.usefulLifeMonths || 36,
        [Validators.required, Validators.min(1)],
      ],
      depreciationMethod: [
        data.asset?.depreciationMethod || DepreciationMethod.LINEAR,
        [Validators.required],
      ],

      assetAccount: [data.asset?.assetAccount || "", [Validators.required]],
      depreciationAccount: [data.asset?.depreciationAccount || "", [Validators.required]],

      isActive: [data.asset?.isActive ?? true],
    })
  }

  ngOnInit() {
    this.loadEntities()
    this.setupAccountAutocomplete()
  }

  loadEntities() {
    this.entityService.getEntities().subscribe((entities) => this.entities.set(entities))
  }

  setupAccountAutocomplete() {
    // Asset Account (Filter for ASSET types)
    this.form
      .get("assetAccount")
      ?.valueChanges.pipe(
        startWith(""),
        debounceTime(300),
        switchMap((value) => {
          const query = typeof value === "string" ? value : value?.accountName
          return this.ledgerService.getAccounts(query || "", 1, 50, LedgerAccountType.ASSET)
        }),
      )
      .subscribe((accounts) => this.filteredAssetAccounts.set(accounts))

    // Depreciation Account (Filter for EXPENSE types)
    this.form
      .get("depreciationAccount")
      ?.valueChanges.pipe(
        startWith(""),
        debounceTime(300),
        switchMap((value) => {
          const query = typeof value === "string" ? value : value?.accountName
          // Ideally filter for Depreciation expense accounts (Class 6 in SKR04)
          return this.ledgerService.getAccounts(query || "", 1, 50, LedgerAccountType.EXPENSE)
        }),
      )
      .subscribe((accounts) => this.filteredDepreciationAccounts.set(accounts))
  }

  displayFn(account: LedgerAccount): string {
    return account && account.accountCode ? `${account.accountCode} - ${account.accountName}` : ""
  }

  save() {
    if (this.form.invalid) return

    const formValue = this.form.value

    const payload: any = {
      ...formValue,
      assetAccountId:
        typeof formValue.assetAccount === "object"
          ? formValue.assetAccount.id
          : formValue.assetAccount,
      depreciationAccountId:
        typeof formValue.depreciationAccount === "object"
          ? formValue.depreciationAccount.id
          : formValue.depreciationAccount,
      purchaseDate: formatDateForAPI(formValue.purchaseDate)!,
    }

    // Add transaction_id if creating from a transaction
    if (this.data.transaction && !this.isEditMode) {
      payload.transactionId = this.data.transaction.transactionId
    }

    // Cleanup objects from payload
    delete payload.assetAccount
    delete payload.depreciationAccount

    const request$ = this.isEditMode
      ? this.assetService.updateAsset(this.data.asset!.id, payload)
      : this.assetService.createAsset(payload)

    request$.subscribe({
      next: (result) => {
        this.snackBar.open(
          `Asset ${this.isEditMode ? "updated" : "created"} successfully`,
          "Close",
          { duration: 3000 },
        )
        this.dialogRef.close(result)
      },
      error: (err) => {
        console.error("Error saving asset", err)
        this.snackBar.open("Error saving asset", "Close", { duration: 3000 })
      },
    })
  }

  cancel() {
    this.dialogRef.close()
  }
}
